import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import PropertyMultiselectInput from 'shared/components/filters/PropertyMultiselectInput';
import SlackChannelPicker from 'features/agents/WorkflowEditor/SlackChannelPicker';
import AgentModelField from 'features/agents/WorkflowEditor/AgentModelField';
import { DecisionModelField } from 'features/agents/WorkflowEditor/fields/decide';
import { detectedTimezone, timezoneOptions } from 'features/agents/WorkflowEditor/scheduleCron';
import { listAlerts } from 'features/alerts/api/alertsApi';
import { getGithubIntegration, getLinearTeams, listLinkedGithubRepos } from 'features/agents/api/agentsApi';
import DatasetPicker from 'features/agents/WorkflowEditor/fields/DatasetPicker';

const LABEL = 'mb-1 block text-[11px] font-medium text-[color:var(--text-secondary)]';
const NOTE = 'text-[11.5px] text-[color:var(--text-muted)]';

const CODING_AGENTS = [
  { value: 'auto', label: 'Whichever is connected' },
  { value: 'claudecode', label: 'Claude Code' },
  { value: 'cursor', label: 'Cursor' },
];

/** The values a template starts with: its own defaults, the person's timezone for schedules. */
export function initialValues(template) {
  const out = {};
  for (const p of template?.params || []) {
    if (p.type === 'alerts') out[p.key] = [];
    else if (p.type === 'model' || p.type === 'decision_model') out[p.key] = {};
    else if (p.type === 'timezone') out[p.key] = p.default && p.default !== 'UTC' ? p.default : detectedTimezone();
    else out[p.key] = p.default ?? '';
  }
  return out;
}

const filled = (p, v) => {
  if (p.type === 'alerts') return Array.isArray(v) && v.length > 0;
  if (p.type === 'model') return !!(v && v.credential_id && v.model);
  if (p.type === 'decision_model') return !!(v && v.credential_id);
  return !!String(v ?? '').trim();
};

/**
 * What still stands between the person and Create: each required choice not
 * made yet, and the GitHub App when the template needs it and it is missing.
 */
export function blockersFor(template, values, ctx = {}) {
  const out = [];
  for (const p of template?.params || []) {
    if (!p.required || filled(p, values?.[p.key])) continue;
    if (p.type === 'alerts') out.push('Choose at least one alert.');
    else if (p.type === 'slack_channel') out.push('Choose a Slack channel.');
    else if (p.type === 'github_repo') out.push('Choose the repository.');
    else if (p.type === 'model') out.push('Choose a model.');
    else if (p.type === 'decision_model') out.push('Choose a decision model.');
    else if (p.type === 'linear_team') out.push('Choose a Linear team.');
    else out.push(`Fill in ${String(p.label || p.key).toLowerCase()}.`);
  }
  const needsRepo = (template?.params || []).some((p) => p.type === 'github_repo');
  if (needsRepo && ctx.githubApp === false) out.unshift('Install the GitHub App first (Settings, Integrations, GitHub).');
  return out;
}

/** Loads what the template's pickers list. `githubApp` is null until known. */
export function useParamsContext(template) {
  const types = useMemo(() => new Set((template?.params || []).map((p) => p.type)), [template]);
  const [alerts, setAlerts] = useState(null);
  const [repos, setRepos] = useState(null);
  const [githubApp, setGithubApp] = useState(null);
  const [teams, setTeams] = useState(null);

  useEffect(() => {
    let off = false;
    if (types.has('alerts')) {
      Promise.resolve(listAlerts()).then((r) => { if (!off) setAlerts(Array.isArray(r) ? r : (r?.alerts || [])); }).catch(() => { if (!off) setAlerts([]); });
    }
    if (types.has('github_repo')) {
      Promise.resolve(getGithubIntegration()).then((g) => { if (!off) setGithubApp(g && g.installation_id ? g : false); }).catch(() => { if (!off) setGithubApp(false); });
      Promise.resolve(listLinkedGithubRepos()).then((r) => { if (!off) setRepos(r); }).catch(() => { if (!off) setRepos([]); });
    }
    if (types.has('linear_team')) {
      Promise.resolve(getLinearTeams()).then((r) => { if (!off) setTeams(r || []); }).catch(() => { if (!off) setTeams([]); });
    }
    return () => { off = true; };
  }, [types]);

  return { alerts, repos, githubApp, teams };
}

function Field({ p, children }) {
  return (
    <div>
      <span className={LABEL}>{p.label}{p.required ? '' : ' (optional)'}</span>
      {children}
    </div>
  );
}

/** One picker per template param, using the same pickers as the editor. */
export default function TemplateParamsForm({ template, values, onChange, ctx }) {
  const { team } = useParams();
  const t = encodeURIComponent(team || '');
  const set = (key, v) => onChange({ ...values, [key]: v });
  const zones = useMemo(() => timezoneOptions(detectedTimezone()), []);

  return (
    <div className="flex flex-col gap-4">
      {(template.params || []).map((p) => {
        const v = values[p.key];
        switch (p.type) {
          case 'alerts':
            return (
              <Field key={p.key} p={p}>
                {ctx.alerts === null ? (
                  <div className={NOTE}>Loading alerts</div>
                ) : ctx.alerts.length === 0 ? (
                  <div className={NOTE}>No alerts yet. <a className="underline" href={`/${t}/alerts`}>Create one</a>, then come back.</div>
                ) : (
                  <PropertyMultiselectInput
                    value={v || []}
                    onChange={(next) => set(p.key, (next || []).map(String))}
                    options={ctx.alerts.map((a) => ({ value: String(a.id), label: a.enabled === false ? `${a.name} (paused)` : a.name }))}
                    placeholder="Choose alerts"
                  />
                )}
              </Field>
            );
          case 'slack_channel':
            return (
              <div key={p.key}>
                <SlackChannelPicker
                  field={{ key: p.key, label: p.required ? p.label : `${p.label} (optional)` }}
                  nodeKind="slack_send_message"
                  showHelp={false}
                  value={v || ''}
                  onChange={(next) => set(p.key, next || '')}
                />
              </div>
            );
          case 'github_repo':
            return (
              <Field key={p.key} p={p}>
                {ctx.githubApp === false ? (
                  <div className={NOTE}>The GitHub App is not installed. <a className="underline" href={`/${t}/settings/integrations?integration=github`}>Install it</a>, then come back.</div>
                ) : (
                  <BrandDropdown
                    value={v || ''}
                    onChange={(next) => set(p.key, next || '')}
                    searchable
                    ariaLabel={p.label}
                    placeholder={ctx.repos === null ? 'Loading' : ctx.repos.length ? 'Choose a repository' : 'No linked repositories'}
                    options={(ctx.repos || []).map((r) => ({ value: `${r.owner}/${r.name}`, label: `${r.owner}/${r.name}` }))}
                  />
                )}
              </Field>
            );
          case 'coding_agent':
            return (
              <Field key={p.key} p={p}>
                <BrandDropdown value={v || 'auto'} onChange={(next) => set(p.key, next || 'auto')} options={CODING_AGENTS} ariaLabel={p.label} />
              </Field>
            );
          case 'model':
            return (
              <div key={p.key}>
                <span className={LABEL}>{p.label}</span>
                <AgentModelField value={v || {}} onChange={(next) => set(p.key, next)} />
              </div>
            );
          case 'decision_model':
            return (
              <div key={p.key}>
                <span className={LABEL}>{p.label}</span>
                <DecisionModelField bare value={v || {}} onChange={(next) => set(p.key, next)} />
              </div>
            );
          case 'dataset':
            return (
              <Field key={p.key} p={p}>
                <DatasetPicker value={v || ''} onChange={(next) => set(p.key, next || '')} />
              </Field>
            );
          case 'linear_team':
            return (
              <Field key={p.key} p={p}>
                {ctx.teams && ctx.teams.length === 0 ? (
                  <div className={NOTE}>Connect Linear in <a className="underline" href={`/${t}/settings/integrations?integration=linear`}>Integrations</a> to choose a team.</div>
                ) : (
                  <BrandDropdown
                    value={v || ''}
                    onChange={(next) => set(p.key, next || '')}
                    ariaLabel={p.label}
                    placeholder={ctx.teams === null ? 'Loading' : 'Select team'}
                    options={(ctx.teams || []).map((x) => ({ value: x.id, label: x.key && x.name ? `${x.key}: ${x.name}` : (x.name || x.key || x.id) }))}
                  />
                )}
              </Field>
            );
          case 'timezone':
            return (
              <Field key={p.key} p={p}>
                <BrandDropdown value={v || 'UTC'} onChange={(next) => set(p.key, next || 'UTC')} options={zones} searchable searchPlaceholder="Search cities" visibleLimit={40} ariaLabel={p.label} />
              </Field>
            );
          default:
            return (
              <Field key={p.key} p={p}>
                <input
                  value={v ?? ''}
                  onChange={(e) => set(p.key, e.target.value)}
                  placeholder={p.placeholder || ''}
                  aria-label={p.label}
                  className="h-8 w-full rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)]"
                />
              </Field>
            );
        }
      })}
    </div>
  );
}
